import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_GEOMETRY, useChartTheme } from './chartTheme';
import { ChartTooltip, type ChartTooltipRow } from './ChartTooltip';

/**
 * Ranked horizontal bars — the workhorse of the Analysis view.
 *
 * Horizontal, not vertical, for two reasons that both come from the data: the categories are long
 * strings (service slugs like `multi-step-agentic`, email addresses), which as vertical column labels
 * must be rotated or truncated; and a ranking reads naturally top-to-bottom.
 *
 * Height is `rows × rowHeight`, never an aspect ratio. That is the whole point of going horizontal:
 * bar thickness stays constant and the chart grows downward as rows are added, so a 12-row chart is
 * as readable as a 3-row one. An aspect-ratio chart squashes bars until they alias.
 *
 * ONE data contract
 * ─────────────────
 * Callers project their rows into `RankedBarDatum` rather than the component being generic over
 * their shape. That is deliberate: both call sites already build a projection (a service row reduced
 * to the selected measure, a seat split at its budget), so there is no duplication — and it means
 * the chart has a single, fully-typed contract instead of a generic signature that Recharts' own
 * deferred `DataKey` type cannot verify anyway.
 */

export interface RankedBarDatum {
  /** Stable identity. Colour and React keys derive from this, never from row order. */
  id: string;
  /** The y-axis label. */
  label: string;
  /** The bar, or the first segment of a two-part bar. */
  primary: number;
  /** The second, stacked segment. Omit or 0 when there is none. */
  secondary?: number;
  /** `primary + secondary`. Precomputed because Recharts labels a segment, never a stack. */
  total: number;
  /** Colour override for this row's primary segment — status emphasis follows the entity. */
  color?: string;
  /** Tooltip rows beyond the segments themselves: context the bar does not encode. */
  extras?: ChartTooltipRow[];
}

/**
 * Shortens a category label to what the y-axis can actually show.
 *
 * Recharts CLIPS an over-long axis label rather than ellipsizing it, so a name wider than the axis
 * loses its first characters and renders as "riya.raghunathan" — which reads as corruption, not as
 * truncation. Trimming here means the reader sees a deliberate ellipsis, and the full value is still
 * in the tooltip and the table view.
 */
function fitAxisLabel(label: string, axisWidth: number): string {
  // ~5.6px per character at the 11px tick size, less 10px of tick padding.
  const maxChars = Math.max(6, Math.floor((axisWidth - 10) / 5.6));
  return label.length <= maxChars ? label : `${label.slice(0, maxChars - 1)}…`;
}

export interface RankedBarChartProps {
  rows: RankedBarDatum[];
  primary: { label: string; color: string };
  /** Provide to draw a two-segment stacked bar. */
  secondary?: { label: string; color: string };
  formatValue: (value: number) => string;
  /**
   * Formats the value-axis ticks. Same reason the trend chart has one: the axis has far less room
   * than a tooltip, and full currency precision ("$600,000.00") either clips or collides with its
   * neighbour. An axis conveys the level; the tooltip and the bar labels carry the exact figure.
   */
  formatAxis?: (value: number) => string;
  /** A limit rule — e.g. the budget every plotted seat shares. */
  reference?: { value: number; label: string };
  /** The width available to THIS chart, so it adapts to its column, not the window. */
  containerWidth: number | null;
  /**
   * Fixes the value axis instead of letting Recharts pick a padded maximum.
   *
   * Needed for a SHARE: left to itself the axis rounds past the largest value, so a chart whose
   * measure cannot exceed 100% renders ticks at 105%. A percentage axis showing 105% has told the
   * reader something false about the scale.
   */
  domain?: [number, number];
}

export function RankedBarChart({
  rows,
  primary,
  secondary,
  formatValue,
  formatAxis,
  reference,
  containerWidth,
  domain,
}: RankedBarChartProps) {
  const theme = useChartTheme();
  const isNarrow = containerWidth !== null && containerWidth < 520;

  /*
   * Category labels take a share of the width, floored and capped: too little and every label
   * truncates to nothing, too much and the bars have no room left to encode anything.
   */
  const axisWidth = isNarrow
    ? Math.min(104, Math.max(68, (containerWidth ?? 320) * 0.34))
    : Math.min(170, Math.max(104, (containerWidth ?? 640) * 0.26));

  const height = Math.max(120, rows.length * CHART_GEOMETRY.rowHeight + 28);

  // Labels are fitted to the axis the chart actually has, which depends on its column width — so the
  // same chart trims differently in a half-width column than at full width, as it should.
  /*
   * `unstackedTotal` carries the row total ONLY when the row has no second segment.
   *
   * On a narrow panel the axis is dropped and each bar must carry its own total, and for a stacked
   * bar that label has to sit on the SECOND segment so it lands at the end of the whole stack. But
   * Recharts renders no rectangle for a zero-width segment, so a row with no failures got no label at
   * all — measured on the Runs view at a 470px chart, where only the one service that had failures
   * showed its number and the other four showed nothing, axis included. This field lets the primary
   * bar label exactly the rows the secondary bar cannot, so every row is labelled once.
   */
  const plotted = rows.map((row) => ({
    ...row,
    axisLabel: fitAxisLabel(row.label, axisWidth),
    unstackedTotal: row.secondary ? null : row.total,
  }));

  /*
   * On a narrow panel the x-axis is dropped and every bar carries its value as a label instead.
   * The reverse — keeping the axis and dropping the labels — would push exact values into hover
   * only, and hover does not exist on a touch screen.
   */
  const showAxis = !isNarrow;
  const showBarLabels = isNarrow;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={plotted}
        layout="vertical"
        margin={{ top: reference ? 14 : 0, right: showBarLabels ? 54 : 12, bottom: 0, left: 0 }}
        barSize={CHART_GEOMETRY.barSize}
        // Recharts' keyboard layer: focus the plot, then arrow keys walk the bars and surface the
        // same tooltip a pointer would, so no value is pointer-only.
        accessibilityLayer
      >
        {/* Vertical hairlines only — on a horizontal bar chart those are the value gridlines.
            Solid, never dashed: dashing reads as "threshold" when it is just a grid. */}
        {showAxis && <CartesianGrid stroke={theme.grid} strokeDasharray="0" horizontal={false} />}

        <XAxis
          type="number"
          hide={!showAxis}
          tickFormatter={formatAxis ?? formatValue}
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          tickLine={false}
          axisLine={false}
          tickCount={4}
          {...(domain ? { domain, allowDataOverflow: false } : {})}
        />
        <YAxis
          type="category"
          dataKey="axisLabel"
          width={axisWidth}
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          tickLine={false}
          axisLine={false}
          // Every row gets its label; Recharts otherwise thins them and a ranking with missing
          // names is unreadable.
          interval={0}
        />

        <Tooltip
          cursor={{ fill: theme.hoverBand }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const row = payload[0]?.payload as RankedBarDatum | undefined;
            if (!row) return null;

            const rows: ChartTooltipRow[] = [
              { label: primary.label, value: formatValue(row.primary), color: row.color ?? primary.color },
            ];
            // A zero second segment is not information — omit it rather than printing "$0.00".
            if (secondary && row.secondary) {
              rows.push({
                label: secondary.label,
                value: formatValue(row.secondary),
                color: secondary.color,
              });
            }
            // `row.label`, never `axisLabel`: the tooltip is where the untrimmed name belongs.
            return <ChartTooltip title={row.label} rows={[...rows, ...(row.extras ?? [])]} />;
          }}
        />

        {reference && (
          <ReferenceLine
            x={reference.value}
            stroke={theme.reference}
            strokeWidth={1}
            label={{ value: reference.label, position: 'top', fill: theme.axis, fontSize: 10 }}
          />
        )}

        <Bar
          dataKey="primary"
          stackId="value"
          fill={primary.color}
          // Square the outer end only when a second segment continues the bar, so a two-part bar
          // reads as one bar with a rounded cap rather than two pills jammed together.
          radius={secondary ? [0, 0, 0, 0] : [0, 4, 4, 0]}
          isAnimationActive={false}
        >
          {plotted.map((row) => (
            <Cell key={row.id} fill={row.color ?? primary.color} />
          ))}
          {showBarLabels && (
            <LabelList
              // `unstackedTotal` rather than `total`: on a stacked chart the rows that DO have a
              // second segment are labelled by that segment instead, so labelling them here too
              // would print the number twice — once mid-bar, once at the end.
              dataKey={secondary ? 'unstackedTotal' : 'total'}
              position="right"
              offset={6}
              fill={theme.axis}
              fontSize={10}
              formatter={(value: unknown) => (typeof value === 'number' ? formatValue(value) : '')}
            />
          )}
        </Bar>

        {secondary && (
          <Bar
            dataKey="secondary"
            stackId="value"
            fill={secondary.color}
            radius={[0, 4, 4, 0]}
            isAnimationActive={false}
          >
            {showBarLabels && (
              <LabelList
                // The stack TOTAL, not this segment: labelling the overage would read as the
                // member's whole spend.
                dataKey="total"
                position="right"
                offset={6}
                fill={theme.axis}
                fontSize={10}
                formatter={(value: unknown) => (typeof value === 'number' ? formatValue(value) : '')}
              />
            )}
          </Bar>
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}
